<script setup lang="ts">
import { defineProps, defineEmits, ref } from 'vue'
import { NForm, NFormItem, NInput, NSelect, NButton, FormInst } from 'naive-ui';
import { SearchFilled } from '@vicons/material';
import { useSkillsStore } from "@/stores/skills.store";
import { ProjectFormProps} from "@/ts/interfaces/component-props";
import { projectFormRules } from "@/ts/formValidationRules";

defineProps<ProjectFormProps>()

const emit = defineEmits([ 'submitAction' ])

const skillStore = useSkillsStore()

skillStore.listSkills()

const yearsOfExperienceOptions = [
  {
    label: 'Between 1-3 years',
    value: '1-3'
  },
  {
    label: 'Between 3-5 years',
    value: '3-5'
  },
  {
    label: 'More than 5 years',
    value: '5+'
  },
]

const hourlyRateOptions = [
  {
    label: 'Between $30-50 USD',
    value: '30-50'
  },
  {
    label: 'Between $50-70 USD',
    value: '50-70'
  },
  {
    label: 'Between $70-$100 USD',
    value: '70-100'
  },
]

const formRef = ref<FormInst | null>(null)

const submit = () => {
  formRef.value?.validate((errors) => {
    if (!errors) {
      emit('submitAction')
    }
  })
}
</script>

<template>
  <div class="project-create-form-wrapper">
    <div class="grid-x">
      <h1 class="heading h1 page-title">
        Position details
      </h1>
    </div>

    <n-form ref="formRef" :model="formData" :rules="projectFormRules">
      <div class="project-create-form grid-x">
        <div class="form-column-wrapper cell small-12 medium-6">
          <div class="input-group">
            <label class="body-text-lg text-w-bold">
              Role title
            </label>
            <n-form-item label="Role Title" path="role_title">
              <n-input v-model:value="formData.role_title" size="medium" round placeholder="Type the role title here" class="form-input" />
            </n-form-item>
          </div>

          <div class="input-group">
            <label class="body-text-lg text-w-bold">
              Years of experience
            </label>
            <n-form-item label="Years of experience" path="years_of_experience">
              <n-select v-model:value="formData.years_of_experience" :options="yearsOfExperienceOptions" size="large" placeholder="Select the years of experience" />
            </n-form-item>
          </div>

          <div class="input-group">
            <label class="body-text-lg text-w-bold">
              Budgeted hourly rate (USD)
            </label>
            <n-form-item label="Budgeted hourly rate" path="usd_hourly_rate">
              <n-select v-model:value="formData.usd_hourly_rate" :options="hourlyRateOptions" size="large" placeholder="Select here the expected hourly rate" />
            </n-form-item>
          </div>

          <div class="input-group with-subtext">
            <div class="flex-container flex-dir-column">
              <label class="body-text-lg text-w-bold">
                Main Skills
              </label>
              <span class="body-text-sm text-purple">*Select a maximum of 3 skills</span>
            </div>
            <n-form-item label="Main Skills" path="skills">
              <n-select v-model:value="formData.skills" :options="skillStore.skills" size="large" placeholder="Type here to search for a skill" multiple filterable clearable>
                <template #arrow>
                  <search-filled />
                </template>
              </n-select>
            </n-form-item>
          </div>
        </div>

        <div class="form-column-wrapper cell small-12 medium-6">

          <div class="input-group with-subtext">
            <div class="grid-x flex-dir-column">
              <label class="body-text-lg text-w-bold">
                Secondary Skills
              </label>
              <span class="body-text-sm text-purple">*Select a maximum of 3 skills</span>
            </div>
            <n-form-item label="Secondary Skills" path="complementary_skills">
              <n-select v-model:value="formData.complementary_skills" :options="skillStore.skills" size="large" placeholder="Type here to search for a skill" multiple filterable clearable>
                <template #arrow>
                  <search-filled />
                </template>
              </n-select>
            </n-form-item>
          </div>

          <div class="input-group">
            <label class="body-text-lg text-w-bold">
              Job description
            </label>
            <n-form-item label="Job description" path="job_description">
              <n-input v-model:value="formData.job_description" type="textarea" :autosize="{ minRows: 5 }" size="medium" round placeholder="Feel free to write or copy and paste the job description." class="form-input" />
            </n-form-item>
          </div>

          <div class="input-group">
            <label class="body-text-lg text-w-bold">
              Job description link
            </label>
            <n-form-item label="Job description link" path="job_description_link">
              <n-input v-model:value="formData.job_description_link" type="textarea" :autosize="{ minRows: 2 }" size="medium" round placeholder="You can paste the link to the job description or job posting here." class="form-input" />
            </n-form-item>
          </div>
        </div>
      </div>

      <div class="button-container grid-x align-center">
        <div class="cell small-12 large-4">
          <n-button @click.prevent="submit" class="new-project-button" size="large" tabindex="0">
            SAVE
          </n-button>
        </div>
      </div>
    </n-form>
  </div>
</template>

<style lang="scss">
.project-create {
  &-form-wrapper {
    margin-left: 70px;
  }

  &-form {
    .form-column-wrapper {
      &:nth-child(odd) {
        padding-right: 30px;
      }

      &:nth-child(even) {
        padding-left: 30px;
      }

      .input-group {
        margin-bottom: 6px;

        .n-input, .n-select {
          margin-top: 25px;
        }

        &.with-subtext {
          .n-input, .n-select {
            margin-top: 4px;
          }
        }

        .n-base-selection .n-base-suffix {
          right: 25px;
        }
      }
    }
  }

  .button-container {
    margin-top: 35px;
  }

  .page-title {
    margin-bottom: 50px;
  }
}
</style>